import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../../../components/button";
import { ButtonSize, ButtonVariant } from "../../../../components/button/types";
import { formatDate } from "../../../../utils";
import { useMemo } from "react";

export interface IConsultant {
  id: string;
  name: string;
  platformContentId: string;
}

export interface IAppointment {
  id: string;
  type: "CONSULTING" | "WEBINAR";
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  consultant: IConsultant;
  title: string;
  platformContentId: string;
}

export interface ScheduledAppointmentsProps {
  appointments: any[] | undefined | null;
  assignedConsultantId: string | null | undefined;
}

const formatLocaleDate = (dateString: string | null): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";

  return date.toLocaleDateString(navigator.language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};
const formatTimeRange = (start: string | null, end: string | null): string => {
  if (!start) return "N/A";
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;
  if (isNaN(startDate.getTime())) return "Invalid Time";

  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  const startTime = startDate.toLocaleTimeString(navigator.language, options);
  const endTime =
    endDate && !isNaN(endDate.getTime())
      ? endDate.toLocaleTimeString(navigator.language, options)
      : null;

  return endTime ? `${startTime} - ${endTime}` : startTime;
};

export function ScheduledAppointments({
  appointments = [],
  assignedConsultantId,
}: ScheduledAppointmentsProps) {
  const { t } = useTranslation("appointments");
  const navigate = useNavigate();

  const numberOfConsultingMeetings = useMemo(() => {
    return appointments?.filter((a) => a.type === "CONSULTING").length ?? 0;
  }, [appointments]);

  const meetingThreshold = 4;

  const isMeetingLimitReached = numberOfConsultingMeetings >= meetingThreshold;
  const handleAppointmentClick = (platformContentId: string, type: string) => {
    if (platformContentId && type === "CONSULTING") {
      navigate(`/profile/consulting`);
    } else if (platformContentId && type === "WEBINAR") {
      navigate(`/webinars/${platformContentId}`);
    } else {
      console.warn(
        "Cannot navigate, consultant ID is missing for this appointment."
      );
    }
  };

  const handleScheduleClick = () => {
    navigate(`/profile/consulting/`);
  };

  // --- Render Logic ---

  return (
    <div className="mt-6 mb-8">
      {appointments && appointments.length > 0 ? (
        <div className=" rounded-md">
          {" "}
          {/* ----- Mobile View (< md) ----- */}
          <div className="md:hidden p-1 space-y-2 ">
            {" "}
            {appointments.map((appointment) => {
              const dateDisplay = formatLocaleDate(appointment.startTime);
              const timeDisplay = formatTimeRange(
                appointment.startTime,
                appointment.endTime
              );

              const compositeTitle =
                appointment.type === "CONSULTING"
                  ? `${t("consultingSessionTitle", "Consulting:")} ${
                      appointment.title ?? ""
                    }`
                  : appointment.title ?? t("titleNotAvailable", "No Title");

              return (
                <div
                  key={`${appointment.id}-mobile`}
                  className="block p-4 border border-gray-300 rounded-lg cursor-pointer  shadow-sm bg-Grey-light-3 hover:bg-gray-50"
                  onClick={() =>
                    handleAppointmentClick(
                      appointment.platformContentId,
                      appointment.type
                    )
                  }
                >
                  {/* Title (Top) */}
                  <div className="font-medium text-gray-900 mb-1">
                    {compositeTitle}
                  </div>
                  {/* Date and Time (Bottom) */}
                  <div className="text-sm text-gray-600">
                    {dateDisplay}, {timeDisplay}
                  </div>
                </div>
              );
            })}
          </div>
          {/* ----- Desktop View (>= md) ----- */}
          <div className="hidden md:block rounded-md p-3 bg-Grey-light-3">
            <div className="space-y-3 overflow-x-auto">
              {" "}
              <table className="table-auto w-full ">
                <thead className="w-full border-b ">
                  <tr>
                    <th className="text-left pb-2 font-semibold text-gray-600 ">
                      {t("table.date")}
                    </th>
                    <th className="text-left pb-2 px-2 font-semibold text-gray-600 ">
                      {t("table.time")}
                    </th>
                    <th className="text-left pb-2 px-2 font-semibold text-gray-600 ">
                      {t("table.title")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appointment) => {
                    const dateDisplay = formatLocaleDate(appointment.startTime);
                    const timeDisplay = formatTimeRange(
                      appointment.startTime,
                      appointment.endTime
                    );

                    const compositeTitle =
                      appointment.type === "CONSULTING"
                        ? `${t("consultingSessionTitle", "Consulting:")} ${
                            appointment.title ?? ""
                          }`
                        : appointment.title ??
                          t("titleNotAvailable", "No Title");

                    return (
                      <tr
                        key={`${appointment.id}-desktop`}
                        className="border-b cursor-pointer hover:bg-gray-50 "
                        onClick={() =>
                          handleAppointmentClick(
                            appointment.platformContentId,
                            appointment.type
                          )
                        }
                      >
                        <td className="py-3 pr-2 align-middle ">
                          {dateDisplay}
                        </td>
                        <td className="py-3 px-2 align-middle ">
                          {timeDisplay}
                        </td>
                        <td className="py-3 px-2 align-middle ">
                          {compositeTitle}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        // Empty State
        <div className="text-center text-Grey p-6 border border-gray-300 rounded-md bg-gray-50">
          <p className="text-gray-600">
            {t(
              "emptyStateMessage",
              "Go ahead and schedule your first session to get started with your personal guidance."
            )}
          </p>
        </div>
      )}
      {/* Schedule Button */}
      {!isMeetingLimitReached && (
        <div className="mt-6 flex justify-end">
          <Button
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={handleScheduleClick}
            disabled={!assignedConsultantId}
          >
            {t("schedule-button", "Schedule Consulting Meeting")}
          </Button>
        </div>
      )}
    </div>
  );
}
